import { CheckCircle, AlertCircle, Clock, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useHealth } from '@/hooks/use-health';
import { useAuth } from '@/hooks/use-auth';
import { ROLE_GROUPS } from '@/types/auth';
import { PageHeader } from '@/components/layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Badge, Spinner } from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { DailyStatsCard } from '@/components/features/dashboard/daily-stats-card';
import { formatDate } from '@/lib/utils';

/**
 * Dashboard page - main landing page after login.
 * Shows API health status and user info.
 *
 * Route: /
 */
/** Menschenlesbare Rollen-Labels fuers Dashboard */
const ROLE_LABELS: Record<string, string> = {
  platform_admin: 'Platform Admin',
  platform_it: 'Platform IT',
  platform_ops: 'Platform Ops',
  merchant_product_manager: 'Product Manager',
  merchant_it: 'Merchant IT',
  merchant_viewer: 'Viewer',
};

export function DashboardPage() {
  const { user, hasRole } = useAuth();
  const { data: health, isLoading, error, refetch } = useHealth();
  const { t } = useTranslation('dashboard');

  const getStatusIcon = () => {
    if (isLoading) return <Spinner size="sm" />;
    if (!health || health.status === 'unhealthy') {
      return <AlertCircle className="h-5 w-5 text-destructive" />;
    }
    if (health.status === 'degraded') {
      return <Clock className="h-5 w-5 text-warning" />;
    }
    return <CheckCircle className="h-5 w-5 text-success" />;
  };

  const getStatusBadge = () => {
    if (isLoading) return <Badge variant="secondary">{t('health.loading')}</Badge>;
    if (!health) return <Badge variant="destructive">{t('health.unknown')}</Badge>;
    switch (health.status) {
      case 'healthy':
        return <Badge variant="success">{t('health.healthy')}</Badge>;
      case 'degraded':
        return <Badge variant="warning">{t('health.degraded')}</Badge>;
      case 'unhealthy':
        return <Badge variant="destructive">{t('health.unhealthy')}</Badge>;
      default:
        return <Badge variant="secondary">{health.status}</Badge>;
    }
  };

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description={t('welcome', { email: user ? `, ${user.email}` : '' })}
      />

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* API Health Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">API Status</CardTitle>
            {getStatusIcon()}
          </CardHeader>
          <CardContent>
            {error ? (
              <ApiErrorDisplay error={error} onRetry={() => refetch()} />
            ) : (
              <>
                <div className="mb-2">{getStatusBadge()}</div>
                {health && (
                  <CardDescription>
                    {t('health.apiVersion', { version: health.version })}
                    <br />
                    {t('health.lastChecked')} {formatDate(health.timestamp)}
                  </CardDescription>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* User Info Card */}
        {user && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('user.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div>
                  <span className="text-sm text-muted-foreground">{t('user.email')} </span>
                  <span className="text-sm font-medium">{user.email}</span>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">{t('user.role')} </span>
                  <Badge variant="secondary">
                    {ROLE_LABELS[user.role] ?? user.role}
                  </Badge>
                </div>
                {user.merchantId && (
                  <div>
                    <span className="text-sm text-muted-foreground">Merchant: </span>
                    <span className="text-sm font-mono">{user.merchantId}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">{t('quickActions.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              <li>
                <a href="/webhooks" className="group flex touch-manipulation items-center gap-1.5 rounded px-1.5 py-1 text-primary transition-colors hover:bg-accent coarse:min-h-11">
                  <ChevronRight className="h-3.5 w-3.5 text-brand-accent transition-transform group-hover:translate-x-0.5" />
                  {t('quickActions.viewWebhooks')}
                </a>
              </li>
              {hasRole(ROLE_GROUPS.TX_TRIGGER) && (
                <li>
                  <a href="/transactions/trigger" className="group flex touch-manipulation items-center gap-1.5 rounded px-1.5 py-1 text-primary transition-colors hover:bg-accent coarse:min-h-11">
                    <ChevronRight className="h-3.5 w-3.5 text-brand-accent transition-transform group-hover:translate-x-0.5" />
                    {t('quickActions.startTransaction')}
                  </a>
                </li>
              )}
              {hasRole(ROLE_GROUPS.SERVICE_WRITE) && (
                <li>
                  <a href="/services" className="group flex touch-manipulation items-center gap-1.5 rounded px-1.5 py-1 text-primary transition-colors hover:bg-accent coarse:min-h-11">
                    <ChevronRight className="h-3.5 w-3.5 text-brand-accent transition-transform group-hover:translate-x-0.5" />
                    {t('quickActions.manageServices')}
                  </a>
                </li>
              )}
              {hasRole(ROLE_GROUPS.IQ_SERVICES_ACCESS) && (
                <li>
                  <a href="/iq-services" className="group flex touch-manipulation items-center gap-1.5 rounded px-1.5 py-1 text-primary transition-colors hover:bg-accent coarse:min-h-11">
                    <ChevronRight className="h-3.5 w-3.5 text-brand-accent transition-transform group-hover:translate-x-0.5" />
                    {t('quickActions.iqConfig')}
                  </a>
                </li>
              )}
            </ul>
          </CardContent>
        </Card>

        {/* Daily Stats Card (Spec 469) — full width under the three cards */}
        <DailyStatsCard />
      </div>
    </div>
  );
}
